import { useState } from 'react';
import { Card } from '../components/Ui';

const caseStudies = [
  {
    id: 1,
    company: 'Nile Delta Agribusiness',
    industry: 'Agricultural',
    location: 'Cairo, Egypt',
    contact: 'Ahmed Hassan, Farm Owner',
    image: '/testimonial-ahmed.jpg',
    systemSize: '150 kW',
    category: 'agricultural',
    badge: 'Featured',
    challenge: 'The farm relied on diesel generators for water pumping and facility power, spending over $42,000 annually on fuel costs. Fixed solar panels installed two years prior underperformed due to seasonal sun angle changes.',
    solution: 'DuaAxis deployed a 150 kW dual-axis tracking system across 2 hectares, integrated with existing irrigation infrastructure. AI-driven orientation control adjusts panel angles in real-time based on weather and sun position data.',
    results: [
      { label: 'Energy Increase', value: '45%', icon: '⚡' },
      { label: 'Annual Savings', value: '$18,500', icon: '💰' },
      { label: 'Payback Period', value: '4.2 years', icon: '📅' },
      { label: 'CO₂ Reduced', value: '87 tons/yr', icon: '🌿' },
    ],
    quote: '"We installed DuaAxis trackers on our farm and saw a 42% increase in energy production. The ROI was much faster than we anticipated."',
    timeline: '8 weeks installation',
  },
  {
    id: 2,
    company: 'Red Sea Manufacturing',
    industry: 'Commercial',
    location: 'Alexandria, Egypt',
    contact: 'Fatima Mahmoud, Plant Director',
    image: '/testimonial-fatima.jpg',
    systemSize: '500 kW',
    category: 'commercial',
    badge: 'Large Scale',
    challenge: 'A coastal manufacturing plant faced high electricity costs of $124,000/year and strict sustainability targets set by their European parent company. Salt air corrosion was a concern for any outdoor installation.',
    solution: 'Installed 500 kW of DuaAxis marine-grade trackers with corrosion-resistant anodised aluminium frames. IoT monitoring integrates with the plant\'s SCADA system for unified energy management.',
    results: [
      { label: 'Energy Increase', value: '48%', icon: '⚡' },
      { label: 'Annual Savings', value: '$62,000', icon: '💰' },
      { label: 'Payback Period', value: '3.8 years', icon: '📅' },
      { label: 'CO₂ Reduced', value: '290 tons/yr', icon: '🌿' },
    ],
    quote: '"The IoT monitoring system is fantastic. We track performance in real-time and the system automatically optimises based on weather conditions."',
    timeline: '12 weeks installation',
  },
  {
    id: 3,
    company: 'Giza Heights Community',
    industry: 'Residential',
    location: 'Giza, Egypt',
    contact: 'Mohamed Ali, Community Manager',
    image: '/testimonial-mohamed.jpg',
    systemSize: '80 kW',
    category: 'residential',
    badge: 'Community',
    challenge: 'A 120-unit residential complex needed to reduce shared-area electricity bills and provide subsidised power to residents. Limited rooftop space meant maximising every square metre was critical.',
    solution: 'DuaAxis installed an 80 kW dual-axis system on the main building rooftop, supplemented by a ground-mounted array in the parking area. Smart load balancing distributes generation across all units.',
    results: [
      { label: 'Energy Increase', value: '41%', icon: '⚡' },
      { label: 'Annual Savings', value: '$24,000', icon: '💰' },
      { label: 'Payback Period', value: '4.5 years', icon: '📅' },
      { label: 'CO₂ Reduced', value: '46 tons/yr', icon: '🌿' },
    ],
    quote: '"DuaAxis was the perfect solution for our limited space — generating significantly more power than traditional panels would have."',
    timeline: '6 weeks installation',
  },
  {
    id: 4,
    company: 'Luxor Grand Resort',
    industry: 'Hospitality',
    location: 'Luxor, Egypt',
    contact: 'Layla Ibrahim, General Manager',
    image: '/testimonial-layla.jpg',
    systemSize: '200 kW',
    category: 'commercial',
    badge: 'Premium',
    challenge: 'As a luxury 5-star resort, the property needed solar that would be energy-efficient without compromising the aesthetic experience of its ancient-temple-view gardens and guest areas.',
    solution: 'Architecturally integrated 200 kW of low-profile DuaAxis trackers in designated service zones. Custom powder-coat finish in desert sand blends with the landscape. All cabling run underground.',
    results: [
      { label: 'Energy Increase', value: '52%', icon: '⚡' },
      { label: 'Annual Savings', value: '$24,800', icon: '💰' },
      { label: 'Payback Period', value: '3.5 years', icon: '📅' },
      { label: 'CO₂ Reduced', value: '116 tons/yr', icon: '🌿' },
    ],
    quote: '"Our energy costs decreased by 60% while maintaining our property\'s beautiful appearance. Guests rarely even notice the installation."',
    timeline: '10 weeks installation',
  },
  {
    id: 5,
    company: 'Suez Industrial Park',
    industry: 'Industrial',
    location: 'Suez, Egypt',
    contact: 'Omar Sherif, Facility Manager',
    image: '/testimonial-omar.jpg',
    systemSize: '1.2 MW',
    category: 'utility',
    badge: 'Utility Scale',
    challenge: 'A large industrial park housing 35 tenant businesses needed to reduce operating costs and offer green energy credentials to attract international tenants. Dust storms and extreme heat were key engineering concerns.',
    solution: 'DuaAxis deployed a 1.2 MW ground-mounted array with automated dust-cleaning mechanisms and high-temperature tolerant components. Separate metering allows individual tenant energy billing.',
    results: [
      { label: 'Energy Increase', value: '47%', icon: '⚡' },
      { label: 'Annual Savings', value: '$148,000', icon: '💰' },
      { label: 'Payback Period', value: '4.0 years', icon: '📅' },
      { label: 'CO₂ Reduced', value: '697 tons/yr', icon: '🌿' },
    ],
    quote: '"The durability in harsh industrial environments impressed us. Despite dust, heat, and sandstorms, the trackers have maintained peak performance."',
    timeline: '18 weeks installation',
  },
];

const categories = [
  { id: 'all', label: 'All Industries' },
  { id: 'agricultural', label: 'Agricultural' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'residential', label: 'Residential' },
  { id: 'utility', label: 'Utility Scale' },
];

const CaseStudies = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [expanded, setExpanded] = useState(null);

  const filtered = activeCategory === 'all'
    ? caseStudies
    : caseStudies.filter(c => c.category === activeCategory);

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-white/20 text-white text-sm font-semibold py-2 px-4 rounded-full mb-4">
            Real Results
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Case Studies</h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto mb-8">
            Discover how businesses across Egypt are achieving measurable energy savings with DuaAxis dual-axis solar tracking
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {[
              { value: '500+', label: 'Installations' },
              { value: '45%', label: 'Avg. Energy Gain' },
              { value: '4.1 yrs', label: 'Avg. Payback' },
              { value: '1,200+', label: 'Tons CO₂ Saved' },
            ].map(stat => (
              <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 text-center">
                <div className="text-2xl font-bold">{stat.value}</div>
                <div className="text-sm text-primary-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-10 justify-center">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeCategory === cat.id
                  ? 'bg-primary-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Case Study Cards */}
        <div className="space-y-8">
          {filtered.map(cs => (
            <Card key={cs.id} variant="elevated" padding="none" className="overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3">
                {/* Left: Company Info */}
                <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white p-8 flex flex-col justify-between">
                  <div>
                    <span className="inline-block bg-secondary-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full mb-4">
                      {cs.badge}
                    </span>
                    <h2 className="text-2xl font-bold mb-1">{cs.company}</h2>
                    <p className="text-primary-200 text-sm mb-1">{cs.industry} · {cs.location}</p>
                    <p className="text-primary-200 text-sm">{cs.contact}</p>
                  </div>
                  <div className="mt-8 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-primary-200">System Size</span>
                      <span className="font-semibold">{cs.systemSize}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-primary-200">Timeline</span>
                      <span className="font-semibold">{cs.timeline}</span>
                    </div>
                  </div>
                </div>

                {/* Right: Content */}
                <div className="lg:col-span-2 p-8">
                  {/* Results Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {cs.results.map(r => (
                      <div key={r.label} className="text-center bg-gray-50 rounded-xl p-4">
                        <div className="text-2xl mb-1">{r.icon}</div>
                        <div className="text-xl font-bold text-primary-600">{r.value}</div>
                        <div className="text-xs text-gray-500 mt-1">{r.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="border-l-4 border-secondary-400 pl-4 italic text-gray-600 mb-4">
                    {cs.quote}
                  </blockquote>

                  {/* Expandable Detail */}
                  <button
                    onClick={() => setExpanded(expanded === cs.id ? null : cs.id)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1"
                  >
                    {expanded === cs.id ? 'Hide details' : 'Read full case study'}
                    <svg className={`w-4 h-4 transition-transform ${expanded === cs.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {expanded === cs.id && (
                    <div className="mt-6 space-y-4 border-t border-gray-100 pt-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">The Challenge</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">{cs.challenge}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">The Solution</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">{cs.solution}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-10 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Write Your Success Story?</h2>
          <p className="text-primary-100 mb-8 max-w-2xl mx-auto">
            Join 500+ businesses across Egypt that have transformed their energy costs with DuaAxis solar tracking.
          </p>
          <a
            href="/#contact"
            className="inline-block bg-secondary-400 hover:bg-secondary-300 text-gray-900 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:-translate-y-1 shadow-lg"
          >
            Get Your Free Proposal
          </a>
        </div>
      </div>
    </div>
  );
};

export default CaseStudies;
